/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import resolveStr from './../../../base/ztest/alternative-resolve-str';
import isnanf from '@stdlib/math/base/assert/is-nanf';
import { factory as quantile } from './../../../base/dists/normal/quantile';
import { factory as cdf } from './../../../base/dists/normal/cdf';
import { ndarray as smean } from './../../../strided/smean';
import sqrt from '@stdlib/math/base/special/sqrt';
import abs from '@stdlib/math/base/special/abs';
import Float32Array from '@stdlib/array/float32';
import PINF from '@stdlib/constants/float32/pinf';
import NINF from '@stdlib/constants/float32/ninf';
import f32 from '@stdlib/number/float64/base/to-float32';


// VARIABLES //

var normalCDF = cdf( 0.0, 1.0 );
var normalQuantile = quantile( 0.0, 1.0 );

// Initialize a workspace for storing confidence intervals:
var WORKSPACE = new Float32Array( 2 );


// MAIN //

/**
* Computes a two-sample Z-test for two single-precision floating-point strided arrays using alternative indexing semantics.
*
* @param {PositiveInteger} NX - number of indexed elements in `x`
* @param {PositiveInteger} NY - number of indexed elements in `y`
* @param {(integer|string)} alternative - alternative hypothesis
* @param {number} alpha - significance level
* @param {number} diff - difference in means under the null hypothesis
* @param {PositiveNumber} sigmax - known standard deviation of `x`
* @param {Float32Array} x - first input array
* @param {integer} strideX - stride length for `x`
* @param {NonNegativeInteger} offsetX - starting index for `x`
* @param {PositiveNumber} sigmay - known standard deviation of `y`
* @param {Float32Array} y - second input array
* @param {integer} strideY - stride length for `y`
* @param {NonNegativeInteger} offsetY - starting index for `y`
* @param {Object} out - output results object
* @returns {Object} results object
*
* @example
* import Float32Array from '@stdlib/array/float32';
* import Results from '@stdlib/stats/base/ztest/two-sample/results/float32';
*
* var x = new Float32Array( [ 4.0, 4.0, 6.0, 6.0, 5.0 ] );
* var y = new Float32Array( [ 3.0, 3.0, 5.0, 7.0, 7.0 ] );
*
* var results = new Results();
* var out = sztest2( x.length, y.length, 'two-sided', 0.05, 0.0, 1.0, x, 1, 0, 2.0, y, 1, 0, results );
* // returns {...}
*
* var bool = ( out === results );
* // returns true
*/
function sztest2( NX, NY, alternative, alpha, diff, sigmax, x, strideX, offsetX, sigmay, y, strideY, offsetY, out ) { // eslint-disable-line max-len, max-params
	var pValue;
	var stderr;
	var xmean;
	var ymean;
	var xvar;
	var yvar;
	var stat;
	var alt;
	var q;

	alt = resolveStr( alternative );
	if (
		NX <= 0 ||
		NY <= 0 ||
		isnanf( alpha ) ||
		isnanf( diff ) ||
		isnanf( sigmax ) ||
		isnanf( sigmay ) ||
		sigmax <= 0.0 ||
		sigmay <= 0.0 ||
		alpha < 0.0 ||
		alpha > 1.0
	) {
		WORKSPACE[ 0 ] = NaN;
		WORKSPACE[ 1 ] = NaN;
		out.rejected = false;
		out.alternative = alt;
		out.alpha = NaN;
		out.pValue = NaN;
		out.statistic = NaN;
		out.ci = WORKSPACE;
		out.nullValue = NaN;
		out.xmean = NaN;
		out.ymean = NaN;
		return out;
	}

	// "Cast" input values to single-precision:
	alpha = f32( alpha );
	diff = f32( diff );
	sigmax = f32( sigmax );
	sigmay = f32( sigmay );

	// Compute the standard error of the mean:
	xvar = sigmax * sigmax;
	yvar = sigmay * sigmay;
	stderr = sqrt( ( xvar / NX ) + ( yvar / NY ) );

	// Compute the arithmetic means of the input arrays:
	xmean = smean( NX, x, strideX, offsetX );
	ymean = smean( NY, y, strideY, offsetY );

	// Compute the test statistic (i.e., the z-score, which is the standardized difference between the sample means of x and y, adjusted by the hypothesized difference, in units of the standard error):
	stat = ( xmean - ymean - diff ) / stderr;

	// Compute the p-value and confidence interval...
	if ( alt === 'less' ) {
		pValue = normalCDF( stat );
		q = normalQuantile( 1.0 - alpha );
		WORKSPACE[ 0 ] = NINF;
		WORKSPACE[ 1 ] = diff + ( ( stat + q ) * stderr );
	} else if ( alt === 'greater' ) {
		pValue = 1.0 - normalCDF( stat );
		q = normalQuantile( 1.0 - alpha );
		WORKSPACE[ 0 ] = diff + ( ( stat - q ) * stderr );
		WORKSPACE[ 1 ] = PINF;
	} else { // alt == 'two-sided'
		pValue = 2.0 * normalCDF( -abs( stat ) );
		q = normalQuantile( 1.0 - ( alpha / 2.0 ) );
		WORKSPACE[ 0 ] = diff + ( ( stat - q ) * stderr );
		WORKSPACE[ 1 ] = diff + ( ( stat + q ) * stderr );
	}
	// Return test results:
	out.rejected = ( pValue <= alpha );
	out.alpha = alpha;
	out.pValue = f32( pValue );
	out.statistic = f32( stat );
	out.ci = WORKSPACE;
	out.alternative = alt;
	out.nullValue = f32( diff );
	out.xmean = xmean;
	out.ymean = ymean;
	return out;
}


// EXPORTS //

export default sztest2;
